import Component from '@glimmer/component';
import { service } from '@ember/service';
import Chip from 'tio-ui/components/chip';
import { Section } from 'tio-ui/components/layout';
import Text from 'tio-ui/components/text';
import { t } from 'ember-intl';
import { eq } from 'tio-ui/utilities';
import {
  getVisibleFields,
  getGroupedVisibleFields,
} from '../../../utils/tuition-assistance/financial-details-config.ts';
import type TasApplicationModel from 'tio-data/models/tas-application';
import type { LimitsStatusDataSignature } from '../../../services/tuition-assistance.ts';
import type IntlService from 'ember-intl/services/intl';

export interface FinancialDetailsAdminSummarySignature {
  Args: {
    application: TasApplicationModel;
    limitsData?: LimitsStatusDataSignature;
  };
  Element: HTMLElement;
}

export default class FinancialDetailsAdminSummary extends Component<FinancialDetailsAdminSummarySignature> {
  @service declare intl: IntlService;

  get visibleFields() {
    return getVisibleFields(this.args.application, this.args.limitsData);
  }

  get groupedFields() {
    return getGroupedVisibleFields(this.args.application, this.args.limitsData);
  }

  get pcsCard() {
    return (
      this.args.application.pcsCards?.find((card) => card.isActive) ??
      this.args.application.pcsCards?.[0]
    );
  }

  get pcsCardState() {
    return this.pcsCard?.state;
  }

  get pcsCardStatusLabel() {
    switch (this.pcsCardState) {
      case 'PCS.CardState.ACTIVATED':
        return 'Active';
      case 'PCS.CardState.CREATED':
        return 'Pending Activation';
      case 'PCS.CardState.CANCELED':
        return 'Canceled';
      case 'PCS.CardState.INACTIVATED':
        return 'Inactive';
      default:
        return 'Inactive';
    }
  }

  get pcsCardStatusIntent() {
    switch (this.pcsCardState) {
      case 'PCS.CardState.ACTIVATED':
        return 'success';
      case 'PCS.CardState.CREATED':
        return 'warning';
      case 'PCS.CardState.CANCELED':
      case 'PCS.CardState.INACTIVATED':
      default:
        return 'default';
    }
  }

  get pcsCardStatusDotClass() {
    switch (this.pcsCardState) {
      case 'PCS.CardState.ACTIVATED':
        return 'bg-[var(--success-icon)]';
      case 'PCS.CardState.CREATED':
        return 'bg-[var(--warning-icon)]';
      case 'PCS.CardState.CANCELED':
      case 'PCS.CardState.INACTIVATED':
      default:
        return 'bg-[var(--text-disabled)]';
    }
  }

  formatFieldValue = (
    value: number | null | undefined,
    isPercentage = false,
    isCredits = false
  ) => {
    if (value === null || value === undefined) {
      return this.intl.t('common.not_applicable');
    }

    const normalizedValue = Number(value);
    if (Number.isNaN(normalizedValue)) {
      return this.intl.t('common.not_applicable');
    }

    if (isPercentage) {
      return `${normalizedValue.toFixed(0)}%`;
    }

    if (isCredits) {
      return `${normalizedValue} ${this.intl.t('common.credits')}`;
    }

    return this.intl.formatNumber(normalizedValue / 100, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 2,
    });
  };

  <template>
    <Section class="" data-test-financial-details-admin-summary>
      <:body>
        <div class="max-w-3xl">
          <div
            class="tio-row-between px-4 py-2.5 bg-[var(--bg-sunken)]/50 dark:bg-[var(--bg-surface)]/50 border border-[var(--border-default)]/80 dark:border-[var(--border-default)]/80 rounded-[10px]"
          >
            <Text class="tio-text-base-semibold text-[var(--text-primary)]">
              {{t "common.tas.financial_details_summary.title"}}
            </Text>
          </div>

          {{#each this.groupedFields as |group|}}
            <div class="mt-4" data-test-financial-section={{group.section}}>
              <div class="px-4 py-2 bg-[var(--bg-page)] border-b border-[var(--border-default)]">
                <div class="tio-row-between gap-3">
                  <Text
                    class="tio-text-sm-semibold text-[var(--text-secondary)] uppercase tracking-wide"
                  >
                    {{t group.labelKey}}
                  </Text>
                  {{#if (eq group.section "course-cash-details")}}
                    <Chip
                      @appearance="faded"
                      @intent={{this.pcsCardStatusIntent}}
                      @size="sm"
                      data-test-course-cash-card-status
                    >
                      <span class="tio-inline-row-extra-tight">
                        <span
                          class="h-1.5 w-1.5 rounded-full {{this.pcsCardStatusDotClass}}"
                          aria-hidden="true"
                        ></span>
                        <span>{{this.pcsCardStatusLabel}}</span>
                      </span>
                    </Chip>
                  {{/if}}
                </div>
              </div>
              {{#each group.fields as |field|}}
                <div
                  class="flex py-4 px-4 border-b border-[var(--border-default)] last:border-b-0"
                  data-test-financial-details-field={{field.key}}
                >
                  <div class="w-64 md:w-72 shrink-0">
                    <Text
                      class="tio-text-sm-medium text-[var(--text-tertiary)] md:whitespace-nowrap"
                    >
                      {{t field.labelKey}}
                    </Text>
                  </div>
                  <div class="flex-1">
                    <Text class="tio-text-sm-medium text-[var(--text-secondary)] pl-6">
                      {{this.formatFieldValue field.value field.isPercentage field.isCredits}}
                    </Text>
                  </div>
                </div>
              {{/each}}
            </div>
          {{/each}}
        </div>
      </:body>
    </Section>
  </template>
}
